import React, { useEffect, useState } from "react";
import fetchData from "../../functions/fetchData";
import updateData from "../../functions/updateData";
import Layout from "../../layout/Layout";
import ExportButtons from "../../components/ExportButtons";
import Modal from "../../components/Modal";
import "../../App.css";
import { getHeaders } from "../../utility/getHeader";

const NewPromotors = () => {
  const [promotors, setPromotors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({ name: "" });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch promotors
  const fetchPromotors = async () => {
    try {
      setLoading(true);
      await fetchData("promotors", setPromotors, "id", {});
    } catch (error) {
      setError("Failed to fetch promotors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotors();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Please enter promotor name");
      return;
    }
    try {
      setLoading(true);
      setError("");
      await updateData("promotors", { ...formData, flag: 1 }, {});
      setSuccess("Promotor added successfully!");
      setFormData({ name: "" });
      fetchPromotors();
    } catch (err) {
      setError("Error adding promotor");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (promotor) => {
    setEditingItem(promotor);
    setEditFormData({ name: promotor.name });
    setShowEditModal(true);
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      setError("Please enter promotor name");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const updatePayload = { ...editFormData, flag: editingItem.flag };
      const whereCondition = { id: editingItem.id };
      await updateData("promotors", updatePayload, whereCondition);
      setSuccess("Promotor updated successfully!");
      setShowEditModal(false);
      setEditingItem(null);
      setEditFormData({});
      fetchPromotors();
    } catch (err) {
      setError("Error updating promotor");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (promotor) => {
    if (window.confirm(`Are you sure you want to delete "${promotor.name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        setError("");
        const whereCondition = { id: promotor.id };
        await updateData("promotors", {}, whereCondition, "DELETE");
        setSuccess("Promotor deleted successfully!");
        fetchPromotors();
      } catch (err) {
        setError("Error deleting promotor");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Close modal
  const closeModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setEditFormData({});
  };

  // Filter promotors based on search term
  const filteredPromotors = promotors.filter((promotor) =>
    promotor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    promotor.id?.toString().includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPromotors = filteredPromotors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPromotors.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <Layout>
      <div className="ledger-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Promotor Management</h4>
        </div>

        {error && (
          <div className="alert alert-danger alert-custom">
            <i className="zmdi zmdi-close-circle mr-2"></i>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-custom">
            <i className="zmdi zmdi-check-circle mr-2"></i>
            {success}
          </div>
        )}

        {/* <div className="row mb-4">
          <div className="col-md-6">
            <div className="ledger-stats bg-primary text-white">
              <h4>{totalPromotors}</h4>
              <p>Total Promotors</p>
            </div>
          </div>
        </div> */}

        <div className="row">
          {/* Form Section */}
          <div className="col-md-4">
            <div className="form-section">
              <h6><i className="zmdi zmdi-plus-circle mr-2"></i> Add New Promotor</h6>
              <form onSubmit={handleSubmit} className="ledger-form">
                <div className="form-group">
                  <label className="control-label">Promotor Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter promotor name"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-submit btn-block"
                  disabled={loading}
                >
                  {loading && <span className="loading-spinner"></span>}
                  Add Promotor
                </button>
              </form>
            </div>
          </div>

          {/* Table Section */}
          <div className="col-md-8">
            <div className="table-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6><i className="zmdi zmdi-view-list mr-2"></i> Existing Promotors ({filteredPromotors.length})</h6>
              </div>
              <div className="ledger-table-panel">
                <table className="table table-striped ledger-table">
                  <thead>
                    <tr>
                      <td colSpan={2}>
                        <div className="search-box">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search promotors..."
                            value={searchTerm}
                            onChange={handleSearch}
                          />
                        </div>
                      </td>
                      <td colSpan={2} style={{ textAlign: 'right' }}>
                        <ExportButtons
                          data={filteredPromotors}
                          filename="promotors"
                          title="Promotors Report"
                          columns={[
                            { key: 'id', label: 'ID' },
                            { key: 'name', label: 'Name' },
                            { key: 'flag', label: 'Flag' }
                          ]}
                          size="sm"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Flag</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPromotors.map((promotor) => (
                      <tr key={promotor.id}>
                        <td>{promotor.id}</td>
                        <td>{promotor.name}</td>
                        <td>{promotor.flag}</td>
                        <td className="action-buttons">
                          <button
                            className="btn btn-info btn-xs"
                            title="Edit"
                            onClick={() => handleEdit(promotor)}
                          >
                            <i className="zmdi zmdi-edit"></i>
                          </button>
                          <button
                            className="btn btn-danger btn-xs ml-1"
                            title="Delete"
                            onClick={() => handleDelete(promotor)}
                          >
                            <i className="zmdi zmdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        return (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                  <div className="pagination-info text-center mt-2">
                    <small className="text-muted">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPromotors.length)} of {filteredPromotors.length} entries
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={closeModal}
          title="Edit Promotor"
        >
          <form onSubmit={handleUpdate}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Promotor Name *
              </label>
              <input
                type="text"
                name="name"
                value={editFormData.name || ''}
                onChange={handleEditInputChange}
                placeholder="Enter promotor name"
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                type="button" 
                onClick={closeModal}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  backgroundColor: loading ? '#ccc' : '#007bff',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Updating...' : 'Update Promotor'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </Layout>
  );
};

export default NewPromotors;
